import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toastify } from '#ui-kit';
import { getChangedFields } from '#utils';
import debug from '#services/debug.service';
import { RegionsService } from '#services/regions';
import {
  CreateRegionRequest,
  RegionResponse,
  UpdateRegionRequest,
} from 'types';

const useRegion = () => {
  const regionService = new RegionsService();

  const { regionId } = useParams();

  const [data, setData] = useState<RegionResponse | undefined>(undefined);

  const [loading, setLoading] = useState(false);

  const getRegion = async () => {
    if (regionId) {
      try {
        setLoading(true);

        const result = await regionService.getRegion(regionId);

        setData(result);
      } catch (err) {
        debug.error('🧑🏻‍💻 ~ useRegion ~ getRegion ~ err:', err);

        toastify.error('Произошла ошибка при получении данных региона');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getRegion();
  }, [regionId]);

  const updateRegion = async (values: UpdateRegionRequest) => {
    if (regionId && data) {
      try {
        setLoading(true);

        const changes = getChangedFields(data, values);

        const result = await regionService.updateRegion(regionId, changes);

        setData(result);

        toastify.success('Данные региона успешно изменены');
      } catch (err) {
        debug.error('🧑🏻‍💻 ~ useRegion ~ updateRegion ~ err:', err);

        toastify.error('Произошла ошибка при изменении данных региона');
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteRegion = async () => {
    if (regionId) {
      try {
        setLoading(true);

        await regionService.deleteRegion(regionId);

        toastify.success('Регион успешно удален');
      } catch (err) {
        debug.error('🧑🏻‍💻 ~ useRegion ~ deleteRegion ~ err:', err);

        toastify.error('Произошла ошибка при удалении региона');
      } finally {
        setLoading(false);
      }
    }
  };

  const createRegion = async (values: CreateRegionRequest) => {
    try {
      setLoading(true);

      const result = await regionService.createRegion(values);

      setData(result);

      toastify.success('Регион успешно создан');
    } catch (err) {
      debug.error('🧑🏻‍💻 ~ useRegion ~ createRegion ~ err:', err);

      toastify.error('Произошла ошибка при создании региона');
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, updateRegion, deleteRegion, createRegion };
};

export default useRegion;
